// PlacesManagement.tsx
import React, { useState, useEffect } from 'react';
import {
    Button,
    Table,
    Space,
    Modal,
    Form,
    Input,
    message,
    App,
} from 'antd';
import {
    UserAddOutlined,
    EditOutlined,
    DeleteOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { getRequest,getRequestWithParams, postRequest, deleteRequest } from '../../../../api/httpRequest';

const { Column } = Table;

interface Place {
    id: number;
    city: string;
    university: string;
    address: string;
}

interface Curator {
    id: number;
    email: string;
}

interface PlacesManagementProps {
    olympiadId: number | null;
}

const PlacesManagement: React.FC<PlacesManagementProps> = ({ olympiadId }) => {
    const [places, setPlaces] = useState<Place[]>([]);
    const [loadingPlaces, setLoadingPlaces] = useState(false);
    const [placeModalVisible, setPlaceModalVisible] = useState(false);
    const [editingPlace, setEditingPlace] = useState<Place | null>(null);
    const [placeForm] = Form.useForm();
    
    // Состояния для управления кураторами
    const [curatorsModalVisible, setCuratorsModalVisible] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
    const [curators, setCurators] = useState<Curator[]>([]);
    const [newCuratorEmail, setNewCuratorEmail] = useState('');
    
    const { notification } = App.useApp();

    useEffect(() => {
        loadPlaces();
    }, []);

    const loadPlaces = () => {
        setLoadingPlaces(true);
        getRequest('/server/olympiad/place/list')
            .then(response => {
                setPlaces(response.data || []);
            })
            .catch(error => {
                notification.error({
                    message: 'Ошибка загрузки',
                    description: 'Не удалось загрузить список площадок'
                });
            })
            .finally(() => {
                setLoadingPlaces(false);
            });
    };

    const handleAddPlace = () => {
        setEditingPlace(null);
        placeForm.resetFields();
        setPlaceModalVisible(true);
    };

    const handleEditPlace = (place: Place) => {
        setEditingPlace(place);
        placeForm.setFieldsValue(place);
        setPlaceModalVisible(true);
    };

    const handleDeletePlace = (place: Place) => {
        Modal.confirm({
            title: 'Удаление площадки',
            content: `Вы уверены, что хотите удалить площадку "${place.university}"?`,
            okText: 'Удалить',
            cancelText: 'Отмена',
            okType: 'danger',
            onOk: () => {
                postRequest(`/server/olympiad/place/delete?placeId=${place.id}`, { placeId: place.id })
                    .then(() => {
                        message.success('Площадка успешно удалена');
                        loadPlaces();
                    })
                    .catch(error => {
                        notification.error({
                            message: 'Ошибка удаления',
                            description: 'Не удалось удалить площадку'
                        });
                    });
            }
        });
    };

    const handleSavePlace = () => {
        placeForm.validateFields().then(values => {
            const url = editingPlace 
                ? `/server/olympiad/place/update?placeId=${editingPlace.id}`
                : '/server/olympiad/place/add';

            postRequest(url, values)
                .then(() => {
                    message.success(editingPlace ? 'Площадка обновлена' : 'Площадка добавлена');
                    setPlaceModalVisible(false);
                    loadPlaces();
                })
                .catch(error => {
                    notification.error({
                        message: 'Ошибка сохранения',
                        description: 'Не удалось сохранить площадку'
                    });
                });
        });
    };

    // Функции для управления кураторами
    const handleShowCurators = (place: Place) => {
        setSelectedPlace(place);
        getRequestWithParams('/server/olympiad/place/curators', { placeId: place.id })
            .then(response => {
                setCurators(response.data || []);
                setCuratorsModalVisible(true);
            })
            .catch(error => {
                notification.error({
                    message: 'Ошибка загрузки',
                    description: 'Не удалось загрузить список кураторов'
                });
            });
    };

    const handleAddCurator = () => {
        if (!newCuratorEmail || !selectedPlace) return;

        postRequest('/server/olympiad/place/user', {
            placeId: selectedPlace.id,
            email: newCuratorEmail
        })
            .then(() => {
                message.success('Куратор добавлен');
                setNewCuratorEmail('');
                getRequestWithParams('/server/olympiad/place/curators', { placeId: selectedPlace.id })
                    .then(response => {
                        setCurators(response.data || []);
                    });
            })
            .catch(error => {
                notification.error({
                    message: 'Ошибка добавления',
                    description: 'Не удалось добавить куратора'
                });
            });
    };

    const handleDeleteCurator = (curator: Curator) => {
        Modal.confirm({
            title: 'Удаление куратора',
            content: `Вы уверены, что хотите удалить куратора ${curator.email}?`,
            okText: 'Удалить',
            cancelText: 'Отмена',
            okType: 'danger',
            onOk: () => {
                deleteRequest('/server/olympiad/place/user', { curatorId: curator.id })
                    .then(() => {
                        message.success('Куратор удален');
                        if (selectedPlace) {
                            getRequestWithParams('/server/olympiad/place/curators' , {placeId: selectedPlace.id})
                                .then(response => {
                                    setCurators(response.data || []);
                                });
                        }
                    })
                    .catch(error => {
                        notification.error({
                            message: 'Ошибка удаления',
                            description: 'Не удалось удалить куратора'
                        });
                    });
            }
        });
    };

    return (
        <>
            <div style={{ marginBottom: 16 }}>
                <Button 
                    type="primary" 
                    onClick={handleAddPlace}
                    icon={<UserAddOutlined />}
                >
                    Добавить площадку
                </Button>
            </div>

            <Table 
                dataSource={places} 
                loading={loadingPlaces}
                rowKey="id"
                pagination={{ pageSize: 10 }}
            >
                <Column title="ID" dataIndex="id" key="id" width={80} />
                <Column title="Город" dataIndex="city" key="city" />
                <Column title="Учебное заведение" dataIndex="university" key="university" />
                <Column title="Адрес" dataIndex="address" key="address" />
                <Column
                    title="Действия"
                    key="actions"
                    render={(_, record: Place) => (
                        <Space size="middle">
                            <Button 
                                type="link" 
                                icon={<EditOutlined />}
                                onClick={() => handleEditPlace(record)}
                            >
                                Изменить
                            </Button>
                            <Button 
                                type="link" 
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => handleDeletePlace(record)}
                            >
                                Удалить
                            </Button>
                            <Button 
                                type="link" 
                                icon={<TeamOutlined />}
                                onClick={() => handleShowCurators(record)}
                            >
                                Кураторы
                            </Button>
                        </Space>
                    )}
                />
            </Table>

            {/* Модальное окно для добавления/редактирования площадки */}
            <Modal
                title={editingPlace ? 'Редактирование площадки' : 'Добавление площадки'}
                open={placeModalVisible}
                onOk={handleSavePlace}
                onCancel={() => setPlaceModalVisible(false)}
                okText="Сохранить"
                cancelText="Отмена"
            >
                <Form form={placeForm} layout="vertical">
                    <Form.Item
                        name="city"
                        label="Город"
                        rules={[{ required: true, message: 'Пожалуйста, введите город' }]}
                    >
                        <Input placeholder="Введите город" />
                    </Form.Item>
                    <Form.Item
                        name="university"
                        label="Учебное заведение"
                        rules={[{ required: true, message: 'Пожалуйста, введите название учебного заведения' }]}
                    >
                        <Input placeholder="Введите название учебного заведения" />
                    </Form.Item>
                    <Form.Item
                        name="address"
                        label="Адрес"
                        rules={[{ required: true, message: 'Пожалуйста, введите адрес' }]}
                    >
                        <Input placeholder="Введите точный адрес" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Модальное окно для управления кураторами */}
            <Modal
                title={`Кураторы площадки: ${selectedPlace?.university}`}
                open={curatorsModalVisible}
                onCancel={() => setCuratorsModalVisible(false)}
                footer={null}
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            placeholder="Введите email куратора"
                            value={newCuratorEmail}
                            onChange={(e) => setNewCuratorEmail(e.target.value)}
                        />
                        <Button 
                            type="primary" 
                            onClick={handleAddCurator}
                            icon={<UserAddOutlined />}
                        >
                            Добавить
                        </Button>
                    </Space.Compact>
                </div>

                <Table 
                    dataSource={curators} 
                    rowKey="id"
                    pagination={false}
                    size="small"
                >
                    <Column title="Email" dataIndex="email" key="email" />
                    <Column
                        title="Действия"
                        key="actions"
                        width={100}
                        render={(_, record: Curator) => (
                            <Button 
                                type="link" 
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteCurator(record)}
                            >
                                Удалить
                            </Button>
                        )}
                    />
                </Table>
            </Modal>
        </>
    );
};

export default PlacesManagement;